import { Building2, CalendarDays, CheckSquare, FileText, Handshake, Import, Landmark, LayoutDashboard, Linkedin, Mail, MapPin, Settings, User, Users } from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import { HelpButton } from "../help/HelpPanel";
import { BouquetIcon } from "../misc/BouquetIcon";
import { useZugang } from "../buchhaltung/daten";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const translate = useTranslate();
  const { zugang } = useZugang();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/tasks/*", location.pathname)) {
    currentPath = "/tasks";
  } else if (matchPath("/locations/*", location.pathname)) {
    currentPath = "/locations";
  } else if (matchPath("/events/*", location.pathname)) {
    currentPath = "/events";
  } else if (matchPath("/emails/*", location.pathname)) {
    currentPath = "/emails";
  } else if (matchPath("/blumengruesse/*", location.pathname)) {
    currentPath = "/blumengruesse";
  } else if (matchPath("/linkedin/*", location.pathname)) {
    currentPath = "/linkedin";
  } else if (matchPath("/buchhaltung/*", location.pathname)) {
    currentPath = "/buchhaltung";
  } else {
    currentPath = false;
  }

  return (
    <>
      <nav className="grow sticky top-0 z-30">
        <header className="ldf-app-top bg-secondary">
          <div className="px-4">
            <div className="flex justify-between items-center flex-1">
              <Link
                to="/"
                className="flex items-center gap-2 text-secondary-foreground no-underline"
              >
                <img
                  className="[.light_&]:hidden h-6"
                  src={darkModeLogo}
                  alt={title}
                />
                <img
                  className="[.dark_&]:hidden h-6"
                  src={lightModeLogo}
                  alt={title}
                />
                {/* LDF: nur Logo + „CRM“, der Firmenname steckt im Logo */}
                <h1 className="text-lg font-semibold tracking-tight lg:text-xl">CRM</h1>
              </Link>
              <div>
                <nav className="flex">
                  <NavigationTab
                    Icon={LayoutDashboard}
                    label={translate("ra.page.dashboard")}
                    to="/"
                    isActive={currentPath === "/"}
                  />
                  <NavigationTab
                    Icon={Users}
                    label={translate("resources.contacts.name", {
                      smart_count: 2,
                    })}
                    to="/contacts"
                    isActive={currentPath === "/contacts"}
                  />
                  <NavigationTab
                    Icon={Building2}
                    label={translate("resources.companies.name", {
                      smart_count: 2,
                    })}
                    to="/companies"
                    isActive={currentPath === "/companies"}
                  />
                  <NavigationTab
                    Icon={Handshake}
                    label={translate("resources.deals.name", {
                      smart_count: 2,
                    })}
                    to="/deals"
                    isActive={currentPath === "/deals"}
                  />
                  <NavigationTab
                    Icon={CheckSquare}
                    label={translate("resources.tasks.name", { smart_count: 2 })}
                    to="/tasks"
                    isActive={currentPath === "/tasks"}
                  />
                  <NavigationTab
                    Icon={MapPin}
                    label="Locations"
                    to="/locations"
                    isActive={currentPath === "/locations"}
                  />
                  <NavigationTab
                    Icon={CalendarDays}
                    label="Events"
                    to="/events"
                    isActive={currentPath === "/events"}
                  />
                  <NavigationTab
                    Icon={Mail}
                    label="E-Mails"
                    to="/emails"
                    isActive={currentPath === "/emails"}
                  />
                  <NavigationTab
                    Icon={BouquetIcon}
                    label="Blumengrüße"
                    to="/blumengruesse"
                    isActive={currentPath === "/blumengruesse"}
                  />
                  <NavigationTab
                    Icon={Linkedin}
                    label="LinkedIn"
                    to="/linkedin"
                    isActive={currentPath === "/linkedin"}
                  />
                  {zugang && (
                    <NavigationTab
                      Icon={Landmark}
                      label="Buchhaltung"
                      to="/buchhaltung"
                      isActive={currentPath === "/buchhaltung"}
                    />
                  )}
                </nav>
              </div>
              <div className="flex items-center">
                <HelpButton />
                <ThemeModeToggle />
                <RefreshButton />
                <UserMenu>
                  <ProfileMenu />
                  <CanAccess resource="sales" action="list">
                    <UsersMenu />
                  </CanAccess>
                  <CanAccess resource="configuration" action="edit">
                    <SettingsMenu />
                  </CanAccess>
                  <ImportFromJsonMenuItem />
                  <ChangelogMenuItem />
                </UserMenu>
              </div>
            </div>
          </div>
        </header>
      </nav>
    </>
  );
};

// LDF: Menüpunkte mit Symbol – bis 1535 px Symbol über kurzem Text, ab 1536 px nebeneinander
const NavigationTab = ({
  label,
  to,
  isActive,
  Icon,
}: {
  label: string;
  to: string;
  isActive: boolean;
  Icon: React.ComponentType<{ className?: string }>;
}) => (
  <Link
    to={to}
    title={label}
    // Volle Farbdeckung statt Transparenz: halbtransparente Linien wirken auf Retina-Displays (iPad) verwaschen
    className={`flex flex-col items-center gap-0.5 whitespace-nowrap border-b-2 px-2 py-2 text-[11px] text-secondary-foreground transition-colors lg:px-2.5 2xl:flex-row 2xl:gap-1.5 2xl:px-3 2xl:py-3 2xl:text-sm ${
      isActive ? "border-secondary-foreground font-semibold" : "border-transparent font-medium hover:border-secondary-foreground/30"
    }`}
  >
    <Icon className={`h-5 w-5 2xl:h-4 2xl:w-4 ${isActive ? "[stroke-width:2.25]" : "[stroke-width:1.75]"}`} />
    {label}
  </Link>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};
export default Header;
